import { Box, Popover, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { CheckCircle, OpenInNew, WarningAmber, Groups } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { PSY_SERVICE, NFB_SERVICE, EEG_SERVICES } from "../../../utils/constants";
import { PSY_BOOKING_PURPLE, WARNING_RED } from "../constants";
import { BookingSlot } from "../types";

export type PopoverReservation = {
    clientId: string;
    clientName: string;
    treatmentId: number;
    missedTwoConsecutiveSessions: boolean;
};

export type PopoverBooking = {
    sessionId: number;
    clientId: string;
    clientName: string;
    treatmentId: number;
    leadId: number | null;
    isClientSession: boolean;
    serviceId: number | null;
    serviceName: string | null;
    title: string | null;
};

type SlotsPopoverInformationProps = {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    reservations: Array<PopoverReservation>;
    bookings: Array<PopoverBooking>;
};

// Projects a calendar BookingSlot down to the fields the popover needs (shared by the PSY cell
// and the NFB block so the projection lives in one place).
export const toPopoverBooking = (booking: BookingSlot) => {
    const popoverBooking: PopoverBooking = {
        sessionId: booking.sessionId,
        clientId: booking.clientId,
        clientName: booking.clientName,
        treatmentId: booking.treatmentId,
        leadId: booking.leadId,
        isClientSession: booking.isClientSession,
        serviceId: booking.serviceId,
        serviceName: booking.serviceName,
        title: booking.title,
    };
    return popoverBooking;
};

const sessionTag = (serviceId: number | null) => {
    if (serviceId === NFB_SERVICE) return "NFB";
    if (serviceId !== null && EEG_SERVICES.includes(serviceId)) return "EEG";
    if (serviceId === PSY_SERVICE) return "Psi";
    return "";
};

type MergedRow = {
    key: string;
    clientLabel: string;
    treatmentId: number;
    leadId: number | null;
    isMeeting: boolean;
    reservation?: PopoverReservation;
    booking?: PopoverBooking;
};

// One row per client, with reservation and session side by side (so the same person isn't listed twice).
const buildRows = (reservations: Array<PopoverReservation>, bookings: Array<PopoverBooking>): Array<MergedRow> => {
    const rowsByKey = new Map<string, MergedRow>();

    reservations.forEach((reservation) => {
        const key = reservation.clientId || `t:${reservation.treatmentId}`;
        const existing = rowsByKey.get(key);
        rowsByKey.set(key, {
            key,
            clientLabel: `${reservation.clientId} - ${reservation.clientName}`,
            treatmentId: reservation.treatmentId,
            leadId: existing?.leadId ?? null,
            isMeeting: false,
            reservation,
            booking: existing?.booking,
        });
    });

    bookings.forEach((booking) => {
        const clientKey =
            booking.clientId ||
            (booking.treatmentId ? `t:${booking.treatmentId}` : booking.leadId ? `l:${booking.leadId}` : `s:${booking.sessionId}`);
        const key = booking.isClientSession ? clientKey : `s:${booking.sessionId}`;
        const existing = rowsByKey.get(key);
        const clientLabel = booking.isClientSession
            ? booking.clientId
                ? `${booking.clientId} - ${booking.clientName}`
                : booking.clientName || "Sessão"
            : booking.serviceName || booking.title || "Ocupado";
        rowsByKey.set(key, {
            key,
            clientLabel: existing?.clientLabel ?? clientLabel,
            treatmentId: existing?.treatmentId ?? booking.treatmentId,
            leadId: existing?.leadId ?? booking.leadId,
            isMeeting: !booking.isClientSession,
            reservation: existing?.reservation,
            booking,
        });
    });

    const rows = Array.from(rowsByKey.values());
    return rows;
};

export const SlotsPopoverInformation = ({ anchorEl, onClose, reservations, bookings }: SlotsPopoverInformationProps) => {
    const isOpen = Boolean(anchorEl);
    const rows = buildRows(reservations, bookings);

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
            <Box sx={{ p: 1.5, minWidth: 260 }}>
                <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 1, borderBottom: "1px solid #eee" } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Cliente</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                Reserva
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                Sessão
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const linkTo =
                                row.treatmentId > 0
                                    ? `/treatments/${row.treatmentId}/sessions`
                                    : row.leadId
                                    ? `/leads/${row.leadId}/sessions`
                                    : null;
                            return (
                                <TableRow key={row.key}>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            {row.isMeeting && <Groups sx={{ fontSize: 15, color: PSY_BOOKING_PURPLE }} />}
                                            {linkTo ? (
                                                <Box
                                                    component={Link}
                                                    to={linkTo}
                                                    target="_blank"
                                                    sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "secondary.main", textDecoration: "none" }}
                                                >
                                                    <Typography variant="body2">{row.clientLabel}</Typography>
                                                    <OpenInNew sx={{ fontSize: 13 }} />
                                                </Box>
                                            ) : (
                                                <Typography variant="body2">{row.clientLabel}</Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.reservation ? (
                                            row.reservation.missedTwoConsecutiveSessions ? (
                                                <WarningAmber sx={{ fontSize: 16, color: WARNING_RED }} titleAccess="Faltaram 2 sessões consecutivas" />
                                            ) : (
                                                <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                                            )
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.booking ? (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25 }}>
                                                <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                                                {sessionTag(row.booking.serviceId) && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {sessionTag(row.booking.serviceId)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        </Popover>
    );
};
